import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, ReactElement, useEffect } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { useVitalsAlertConfigForm } from 'src/features/admin/vitals-alert-config/useVitalsAlertConfigForm';
import { VitalsAlertConfigFields } from 'src/features/admin/vitals-alert-config/VitalsAlertConfigFields';
import useEvolveUser from 'src/hooks/useEvolveUser';
import { useProgressNoteConfig, useUpdateProgressNoteConfig } from 'src/hooks/useProgressNoteConfig';
import { mapDispositionTypeToLabel } from 'utils/lib/fhir/disposition';
import {
  ProgressNoteConfig,
  UpdateProgressNoteConfigInputSchema,
  VITALS_HEADER_UNIT_SYSTEM_LABELS,
  VITALS_HEADER_UNIT_SYSTEMS,
  VITALS_UNIT_INPUT_ORDER_LABELS,
  VITALS_UNIT_INPUT_ORDERS,
} from 'utils/lib/types/api/progress-note-config/progress-note-config.types';
import { RoleType } from 'utils/lib/types/api/user.types';
import { DEFAULT_PROGRESS_NOTE_CONFIG } from 'utils/lib/utils/progress-note-config';

const FORM_MAX_WIDTH = 960;

type ProgressNoteSelectFieldName = 'vitalsUnitInputOrder' | 'vitalsHeaderUnitSystem';
type ProgressNoteTextFieldName = Exclude<keyof ProgressNoteConfig, 'mdmRequired' | ProgressNoteSelectFieldName>;

interface ConfigTextAreaFieldProps {
  control: Control<ProgressNoteConfig>;
  name: ProgressNoteTextFieldName;
  label: string;
  minRows?: number;
}

const ConfigTextAreaField = ({ control, name, label, minRows = 2 }: ConfigTextAreaFieldProps): ReactElement => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <TextField
        {...field}
        label={label}
        multiline
        minRows={minRows}
        fullWidth
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
      />
    )}
  />
);

interface ConfigSelectFieldProps<Option extends string> {
  control: Control<ProgressNoteConfig>;
  name: ProgressNoteSelectFieldName;
  label: string;
  helperText: string;
  options: readonly Option[];
  optionLabels: Record<Option, string>;
}

const ConfigSelectField = <Option extends string>({
  control,
  name,
  label,
  helperText,
  options,
  optionLabels,
}: ConfigSelectFieldProps<Option>): ReactElement => {
  const labelId = `${name}-label`;
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <FormControl fullWidth>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select labelId={labelId} label={label} value={value} onChange={(event) => onChange(event.target.value)}>
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {optionLabels[option]}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
      )}
    />
  );
};

export default function ProgressNoteAdminPage(): ReactElement {
  const { data, isPending, isError } = useProgressNoteConfig();
  const { mutate, isPending: isSubmitting } = useUpdateProgressNoteConfig();
  const isCustomerSupport = useEvolveUser()?.hasRole([RoleType.CustomerSupport]) ?? false;

  const {
    control,
    formState: { isDirty },
    getValues,
    trigger,
    reset,
  } = useForm<ProgressNoteConfig>({
    defaultValues: DEFAULT_PROGRESS_NOTE_CONFIG,
    resolver: zodResolver(UpdateProgressNoteConfigInputSchema),
  });

  const vitalsAlerts = useVitalsAlertConfigForm();

  useEffect(() => {
    if (!data) return;
    reset(
      {
        ...DEFAULT_PROGRESS_NOTE_CONFIG,
        ...data,
      },
      { keepDirtyValues: true }
    );
  }, [data, reset]);

  const progressNoteEditable = !isPending && !isError;
  const vitalsAlertsEditable = !vitalsAlerts.isPending && !vitalsAlerts.isError;
  const anySubmitting = isSubmitting || vitalsAlerts.isSubmitting;
  const anyDirty = (progressNoteEditable && isDirty) || (vitalsAlertsEditable && vitalsAlerts.isDirty);

  const handleSave = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const [progressNoteValid, vitalsAlertsValid] = await Promise.all([
      progressNoteEditable ? trigger() : true,
      vitalsAlertsEditable ? vitalsAlerts.validate() : true,
    ]);
    if (!progressNoteValid || !vitalsAlertsValid) return;

    if (progressNoteEditable && isDirty) {
      const parsed = UpdateProgressNoteConfigInputSchema.safeParse(getValues());
      if (parsed.success) {
        // The prompt field is customer-support-only, and react-hook-form submits the value it loaded
        // even for a field it never rendered. Omitting it keeps this form from carrying a stale prompt
        // back to the server, where absent means "leave the stored prompt alone".
        const { signReviewPrompt: _signReviewPrompt, ...withoutPrompt } = parsed.data;
        mutate(isCustomerSupport ? parsed.data : withoutPrompt, {
          onSuccess: () => {
            reset(parsed.data);
          },
        });
      }
    }
    if (vitalsAlertsEditable && vitalsAlerts.isDirty) {
      vitalsAlerts.submit();
    }
  };

  const handleDiscard = (): void => {
    if (progressNoteEditable) {
      reset({ ...DEFAULT_PROGRESS_NOTE_CONFIG, ...data });
    }
    if (vitalsAlertsEditable) {
      vitalsAlerts.discard();
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Settings for how providers complete and sign progress notes
      </Typography>

      <Paper component="form" onSubmit={handleSave} sx={{ p: 3, maxWidth: FORM_MAX_WIDTH }}>
        <Stack spacing={3}>
          {isPending ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error">Failed to load the current progress note settings.</Alert>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Assessment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Standard content used when Medical Decision Making is pre-filled for a new note.
                </Typography>
                <ConfigTextAreaField
                  control={control}
                  name="medicalDecisionDefaultText"
                  label="Default Medical Decision Making content"
                  minRows={4}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Disposition
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Default content displayed when a disposition option is selected
                </Typography>
                <Stack spacing={2}>
                  <ConfigTextAreaField
                    control={control}
                    name="pcpNoTypeDispositionDefaultText"
                    label={mapDispositionTypeToLabel['pcp-no-type']}
                  />
                  <ConfigTextAreaField
                    control={control}
                    name="anotherDispositionDefaultText"
                    label={mapDispositionTypeToLabel.another}
                  />
                  <ConfigTextAreaField
                    control={control}
                    name="edDispositionDefaultText"
                    label={mapDispositionTypeToLabel.ed}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Controller
                  name="mdmRequired"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={<Switch checked={value} onChange={(_event, checked) => onChange(checked)} />}
                      label="MDM required for sign and close"
                    />
                  )}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Vitals
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Units used when vitals are entered and when they are shown to the provider
                </Typography>
                <Stack spacing={2}>
                  <ConfigSelectField
                    control={control}
                    name="vitalsUnitInputOrder"
                    label="Vital measurement unit input order"
                    helperText="Order of the unit input fields when a vital is entered (e.g. weight, height, temperature)"
                    options={VITALS_UNIT_INPUT_ORDERS}
                    optionLabels={VITALS_UNIT_INPUT_ORDER_LABELS}
                  />
                  <ConfigSelectField
                    control={control}
                    name="vitalsHeaderUnitSystem"
                    label="Vitals display units in visit header"
                    helperText="Units the visit header shows the patient's weight in"
                    options={VITALS_HEADER_UNIT_SYSTEMS}
                    optionLabels={VITALS_HEADER_UNIT_SYSTEM_LABELS}
                  />
                </Stack>
              </Box>

              {/* Field is hidden for non-CustomerSupport users, but its value still round-trips unchanged:
                  react-hook-form submits values carried in defaultValues/reset even when the field is never rendered. */}
              {isCustomerSupport && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                      Note review at signing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Requirements checked against the note when a provider opens Review &amp; Sign. Anything not met is
                      shown to the provider as an informational warning — it never blocks signing. Write it as
                      instructions to a reviewer, e.g. &quot;Confirm at least 4 ROS systems are documented with at least
                      one item each&quot;. Leave blank to turn the review off.
                    </Typography>
                    <ConfigTextAreaField
                      control={control}
                      name="signReviewPrompt"
                      label="Note review requirements"
                      minRows={4}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          <Divider />

          {vitalsAlerts.isPending ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress />
            </Box>
          ) : vitalsAlerts.isError ? (
            <Alert severity="error">Failed to load the current vital alert levels.</Alert>
          ) : (
            <VitalsAlertConfigFields form={vitalsAlerts} />
          )}

          <Divider />

          <Stack direction="row" spacing={1}>
            <LoadingButton type="submit" variant="contained" loading={anySubmitting} disabled={!anyDirty}>
              Save
            </LoadingButton>
            <LoadingButton
              type="button"
              variant="outlined"
              disabled={anySubmitting || !anyDirty}
              onClick={handleDiscard}
            >
              Discard changes
            </LoadingButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
